import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react'

import SidebarHeader from '@/components/Sidebar/SidebarHeader/SidebarHeader'
import SidebarSection from '@/components/Sidebar/SidebarSection/SidebarSection'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { UserProfileCard } from '@/components/UserProfileCard'
import { useAuthStore } from '@/features/auth/authStore'
import GlobalSearchDialog from '@/features/global-search/components/GlobalSearchDialog'
import { useGlobalSearchStore } from '@/features/global-search/store'
import {
  NotificationStatus,
  useListNotifications,
  useWebPubSub,
} from '@/features/notification'
import { NAVIGATION_SECTIONS } from '@/features/sidebar/constants/navigationConfig.ts'
import { useSidebarStore } from '@/features/sidebar/store/sidebarStore'
import { SidebarSectionConfig } from '@/features/sidebar/types'
import { ERoleType } from '@/features/user-management/constants'
import { useCurrentUser, useLogout } from '@/hooks/useAuth'
import { cn } from '@/utils/common'
import { DEFAULT_SEARCH_REQUEST_BODY } from '@/utils/constants'
import { ROUTES } from '@/utils/routes'

import { Input } from '..'

const COLLAPSED_WIDTH = 64 // px

export interface SidebarProps {
  width?: number
}

const Sidebar: React.FC<SidebarProps> = ({ width = 296 }) => {
  const navigate = useNavigate()
  const { data: user } = useCurrentUser()
  const logoutMutation = useLogout()
  const webPubSubClient = useWebPubSub()
  const { setDialogOpen } = useGlobalSearchStore()
  const { isSidebarCollapsed, setSidebarCollapsed } = useSidebarStore()
  const userRoles = useAuthStore.getState().getRoles()

  const hasAccess = (roles?: ERoleType[]) => {
    if (!roles || roles.length === 0) {
      return true
    }
    return userRoles.some((role) => roles.includes(role))
  }

  const filterVisibleItems = (
    items: (typeof NAVIGATION_SECTIONS)[0]['items']
  ) => {
    return items
      .map((item) => {
        if ('children' in item) {
          const visibleChildren = item.children.filter((child) =>
            hasAccess(child.roles)
          )

          if (visibleChildren.length === 0) {
            return null
          }

          return {
            ...item,
            children: visibleChildren,
          }
        }

        return hasAccess(item.roles) ? item : null
      })
      .filter(Boolean) as typeof items
  }

  const roleFilteredSections = useMemo<SidebarSectionConfig[]>(() => {
    if (!userRoles || userRoles.length === 0) {
      return []
    }

    return NAVIGATION_SECTIONS.map((section) => {
      const visibleItems = filterVisibleItems(section.items)

      if (visibleItems.length === 0) {
        return null
      }

      return {
        ...section,
        items: visibleItems,
      }
    }).filter((section): section is SidebarSectionConfig => Boolean(section))
  }, [userRoles])

  const { data: unreadNotifications } = useListNotifications({
    offset: 0,
    limit: DEFAULT_SEARCH_REQUEST_BODY.limit ?? 20,
    sort_by: '-created_at',
    filter_by: { status: [NotificationStatus.Unread] },
  })

  // Update navigation sections with real unread count
  const navigationSections = useMemo(() => {
    const unreadCount = unreadNotifications?.pagination?.total_items ?? 0

    return roleFilteredSections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.id === 'notifications') {
          return { ...item, badge: unreadCount }
        }
        return item
      }),
    }))
  }, [roleFilteredSections, unreadNotifications])

  const handleLogout = async () => {
    void webPubSubClient.disconnect()
    await logoutMutation.mutateAsync()
  }

  const handleOpenGlobalSearchDialog = () => {
    setDialogOpen(true)
  }

  const handleSettingsClick = () => {
    navigate(ROUTES.NOTIFICATION_SETTINGS)
  }

  const sidebarWidth = React.useMemo(
    () => (isSidebarCollapsed ? `${COLLAPSED_WIDTH}px` : `${width}px`),
    [isSidebarCollapsed, width]
  )

  return (
    <aside
      className={cn(
        'z-30 relative flex-shrink-0 h-full',
        'flex flex-col',
        'bg-[var(--Colors-Background-bg-tertiary)]',
        'border-r border-[var(--Colors-Border-border-secondary)]',
        'transition-all duration-300',
        isSidebarCollapsed ? 'px-2 pt-5 pb-4' : `p-5 pb-4`
      )}
      style={{
        width: sidebarWidth,
        flex: `0 0 ${sidebarWidth}`,
      }}
    >
      {/* Header */}
      <div
        className={cn(
          'pb-5 flex',
          isSidebarCollapsed ? 'justify-center' : 'justify-between items-center'
        )}
      >
        {!isSidebarCollapsed && <SidebarHeader />}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="flex-shrink-0 p-0 size-8 hover:bg-gray-50 group"
              onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={
                isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
              }
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
              ) : (
                <PanelLeftClose className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <span>{isSidebarCollapsed ? 'Expand' : 'Collapse'}</span>
          </TooltipContent>
        </Tooltip>
      </div>

      {isSidebarCollapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="p-5 mx-auto bg-white size-8 hover:bg-gray-100"
              onClick={handleOpenGlobalSearchDialog}
              aria-label="Search"
            >
              <Search size={20} className="text-gray-400" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Search</TooltipContent>
        </Tooltip>
      ) : (
        <Input
          placeholder="Search"
          className="w-full cursor-pointer"
          leadingIcon={<Search size={20} className="text-gray-400" />}
          readOnly
          onClick={handleOpenGlobalSearchDialog}
        />
      )}

      {/* Scrollable Navigation Sections*/}
      <div
        className={cn(
          'flex-1 pt-8',
          'no-scrollbar overflow-y-auto overflow-x-hidden'
        )}
      >
        {navigationSections.map((section) => (
          <div key={section.title} style={{ paddingBottom: '20px' }}>
            <SidebarSection
              key={section.title}
              config={section}
              isCollapsed={isSidebarCollapsed}
            />
          </div>
        ))}
      </div>

      {/* User Profile Card */}
      {user && (
        <UserProfileCard
          user={user}
          onLogout={handleLogout}
          onSettingsClick={handleSettingsClick}
          isCollapsed={isSidebarCollapsed}
        />
      )}
      <GlobalSearchDialog />
    </aside>
  )
}

Sidebar.displayName = 'Sidebar'

export default React.memo(Sidebar)
